/// <amd-module name="Wit/Settings/BoardsExternalIntegration/components/panels/addRepoManually/AddRepoManuallyPanel" />
import * as React from "react";
import { ITfsPageService } from "Tfs/Platform/Page";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { VssComponent } from "VSS/Platform/Layout";
import { Button } from "VSSUI/Button";
import { FormItem } from "VSSUI/FormItem";
import { TextField } from "VSSUI/TextField";
import { KeyCode } from "VSSUI/Util";
import { ExternalGitRepo } from "Wit/Clients/Internal/Contracts/ExternalEvents";
import { GitHubRepositoryDataProviderRequest } from "Wit/Clients/Internal/Contracts/WorkItemTracking";
import { showConfirmationDialog } from "Wit/Settings/BoardsExternalIntegration/components/common";
import { getTextWithServiceName } from "Wit/Settings/BoardsExternalIntegration/components/common/Utilities";
import { IAddRepoManuallyPanelProps } from "Wit/Settings/BoardsExternalIntegration/components/panels/addRepoManually/AddRepoManuallyPanel.Props";
import { BasePanel } from "Wit/Settings/BoardsExternalIntegration/components/panels/BasePanel";
import * as Resources from "Wit/Settings/BoardsExternalIntegration/Resources";
import { GitHubIntegrationCIActions, publishGitHubIntegrationTelemetry } from "Wit/Settings/BoardsExternalIntegration/telemetry/GitHub";

interface IRepositoryInfo {
    name: string;
    repoId: string;
}

interface IAddRepoManuallyPanelState {
    repositories: IRepositoryInfo[];
    repoName: string;
    repoId?: string;
    textfieldErrorMessage?: string;
    panelErrorMessage?: string;
    loadingSpinnerMessage?: string;
    isDirty: boolean;
}

const GitHubRepositoryDataProviderId = "ms.vss-work-web.github-user-repository-data-provider";

export class AddRepoManuallyPanel extends VssComponent<IAddRepoManuallyPanelProps, IAddRepoManuallyPanelState> {
    private readonly _textFieldRef = React.createRef<TextField>();

    constructor(props: IAddRepoManuallyPanelProps) {
        super(props);
        this.state = {
            repositories: [],
            repoName: "",
            isDirty: false
        };
    }

    public componentDidMount() {
        this._focusOnInput();
    }

    public render() {
        return (
            <BasePanel
                onDismiss={this._onDismiss}
                title={getTextWithServiceName(this.props.repositoryType, Resources.AddRepo_Title)}
                onRenderHeaderContent={this._renderHeader}
                onRenderPanelFooter={this._renderFooter}
                errorMessage={this.state.panelErrorMessage}
                className="add-repo-manually-panel"
                loadingSpinnerMessage={this.state.loadingSpinnerMessage}
            >
                <div className="content-header">
                    {getTextWithServiceName(this.props.repositoryType, Resources.AddRepoManually_ContentHeader)}{" "}
                    <span>{Resources.AddRepoManually_OrgRepo}</span> {Resources.AddRepoManually_Or}{" "}
                    <span>{Resources.AddRepoManually_AccountRepo}</span>.
                </div>
                <div className="panel-content">
                    {this._renderList()}
                    <div className="repository-row">
                        <FormItem className="input-field" error={!!this.state.textfieldErrorMessage} message={this.state.textfieldErrorMessage}>
                            <TextField
                                onBlur={this._validateRepo}
                                onChange={this._onItemChanged}
                                onKeyDown={this._onKeyDown}
                                placeholder={Resources.AddRepoManually_placeholder}
                                value={this.state.repoName}
                                suffixIconProps={{
                                    iconName: "Error",
                                    className: "repository-error"
                                }}
                                ref={this._textFieldRef}
                            />
                        </FormItem>
                    </div>
                    <div>
                        <Button
                            className="add-button"
                            iconProps={{ iconName: "Add", className: "add-icon" }}
                            subtle={true}
                            onClick={this._addButtonClick}
                            disabled={!this.state.repoName || this.state.repoName.trim() === ""}
                        >
                            {Resources.AddRepoManually_add}
                        </Button>
                    </div>
                </div>
            </BasePanel>
        );
    }

    private _focusOnInput() {
        requestAnimationFrame(() => {
            if (this._textFieldRef.current) {
                this._textFieldRef.current.focus();
            }
        });
    }

    private readonly _renderList = (): JSX.Element[] => {
        return this.state.repositories.map((repo, index) => this._renderItem(repo, index));
    };

    private readonly _renderItem = (repo: IRepositoryInfo, index: number): JSX.Element => {
        return (
            <div key={index} className="repository-row flex-row">
                <div className="flex-grow repo-name">{repo.name}</div>
                <div className="delete-icon-container">
                    <Button subtle={true} iconProps={{ iconName: "Delete" }} onClick={this._onRemoveButtonClick(index)} />
                </div>
            </div>
        );
    };

    private readonly _renderHeader = () => {
        return <div>{getTextWithServiceName(this.props.repositoryType, Resources.AddRepoManually_SubHeader)}</div>;
    };

    private readonly _renderFooter = (): JSX.Element => {
        return (
            <div className="align-right">
                <Button text={Resources.Back} onClick={this._onBackButtonClicked} />
                <Button primary={true} text={Resources.Save} onClick={this._onSaveButtonClicked} disabled={!this._canSave()} />
            </div>
        );
    };

    private _canSave(): boolean {
        return this.state.isDirty && !this.state.textfieldErrorMessage && !this.state.panelErrorMessage;
    }

    private readonly _onBackButtonClicked = () => {
        if (!this.state.isDirty) {
            this.props.onBackClick();
        } else {
            this._showConfirmationDialog(this.props.onBackClick);
        }
    };

    private readonly _onDismiss = () => {
        // Don't dismiss when busy
        if (!this.state.loadingSpinnerMessage) {
            if (!this.state.isDirty) {
                this.props.onDismiss();
            } else {
                this._showConfirmationDialog(this.props.onDismiss);
            }
        }
    };

    private readonly _showConfirmationDialog = (onConfirmation: () => void) => {
        showConfirmationDialog(
            this.context,
            Resources.ConfirmationClosePanelMessage,
            Resources.ConfirmationClosePanelActionText,
            Resources.ConfirmationClosePanelTitle,
            onConfirmation
        );
    };

    private readonly _onSaveButtonClicked = () => {
        this.setState({ loadingSpinnerMessage: Resources.SpinnerBasePanelSavingText }, this._onSave);
    };

    private readonly _onItemChanged = (event: React.ChangeEvent<HTMLInputElement>, value: string) => {
        this.setState({
            repoName: value,
            repoId: undefined,
            textfieldErrorMessage: undefined,
            isDirty: this.state.repositories.length > 0 || value.length > 0
        });
    };

    private readonly _onKeyDown = (event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        if (event.keyCode === KeyCode.enter) {
            this._addButtonClick();
        }
    };

    private readonly _addButtonClick = async (): Promise<void> => {
        const validatedRepo = await this._validateRepo();

        // The repo is valid if it has a repoId
        if (validatedRepo) {
            const repositoriesList = [...this.state.repositories, validatedRepo];
            this.setState({
                repositories: repositoriesList,
                textfieldErrorMessage: undefined,
                repoName: "",
                isDirty: repositoriesList.length > 0 // we're setting reponame to empty and textfieldErrorMessage to undefined here so, we don't include them here
            });
        }
    };

    private readonly _onRemoveButtonClick = (index: number): (() => void) => {
        return () => {
            const newRepositories = [...this.state.repositories];
            newRepositories.splice(index, 1);

            this.setState(
                {
                    repositories: newRepositories,
                    isDirty: newRepositories.length > 0 || this.state.repoName.length > 0
                },
                () => {
                    this._focusOnInput();
                }
            );
        };
    };

    private readonly _onSave = async (): Promise<void> => {
        const { connection, onSave } = this.props;

        if (!this.state.textfieldErrorMessage) {
            let succeeded = true;
            let errorMessage = "";

            const reposToAdd = await this._createReposList();
            const connectionToSave = { ...connection };

            let savedConnection = connectionToSave;
            if (reposToAdd.length > 0) {
                connectionToSave.externalGitRepos = connectionToSave.externalGitRepos.concat(reposToAdd);

                try {
                    savedConnection = await onSave(connectionToSave);
                } catch (error) {
                    succeeded = false;
                    errorMessage = error.message;
                    this.setState({
                        loadingSpinnerMessage: undefined,
                        panelErrorMessage: errorMessage
                    });
                }
            } else {
                this.setState({
                    loadingSpinnerMessage: undefined
                });
            }

            publishGitHubIntegrationTelemetry(this.context, GitHubIntegrationCIActions.ADMIN_ADD_REPOS, {
                addedNumberOfRepos: reposToAdd.length,
                totalNumberOfRepos: savedConnection.externalGitRepos.length,
                providerKey: savedConnection.providerKey,
                authType: savedConnection.serviceEndpoint.authorizationScheme,
                manually: true,
                succeeded: succeeded,
                errorMessage: errorMessage
            });
        }
    };

    private readonly _createReposList = async (): Promise<ExternalGitRepo[]> => {
        const allRepos: ExternalGitRepo[] = [];

        // Add to the newRepos list the repositories we have in the state
        for (const repo of this.state.repositories) {
            allRepos.push({ id: repo.repoId, name: repo.name } as ExternalGitRepo);
        }

        // Check if we have a pending repository in the textfield
        if (this.state.repoName) {
            // Validate the repo if we didn't do it (user didn't click add or onblur was triggered)
            if (!this.state.repoId) {
                const pendingRepo = await this._validateRepo();
                if (pendingRepo && !this.state.repositories.some(repo => repo.repoId === pendingRepo.repoId)) {
                    allRepos.push({ id: pendingRepo.repoId, name: pendingRepo.name } as ExternalGitRepo);
                } else {
                    // If we have a no valid repository we should return empty array to avoid saving the connection
                    return [];
                }
            } else {
                allRepos.push({ id: this.state.repoId, name: this.state.repoName } as ExternalGitRepo);
            }
        }

        return allRepos;
    };

    private readonly _validateRepo = async (): Promise<IRepositoryInfo | null> => {
        let repo: IRepositoryInfo | null = null;

        if (this.state.repoName && this.state.repoName.trim() !== "") {
            const pageService = this.context.pageContext.getService<ITfsPageService>("ITfsPageService");
            const pageData = pageService.getData();
            // tslint:disable-next-line:no-non-null-assertion
            const projectId = pageData!.project!.id;

            let textfieldErrorMessage = "";
            const endpointId = this.props.connection.serviceEndpoint && this.props.connection.serviceEndpoint.id;
            const contributionService: IVssContributionService = this.context.pageContext.getService<IVssContributionService>(
                "IVssContributionService"
            );

            try {
                const externalRepo = await contributionService.getDataAsync<ExternalGitRepo, GitHubRepositoryDataProviderRequest>(
                    GitHubRepositoryDataProviderId,
                    {
                        projectId: projectId,
                        repoWithOwnerName: this.state.repoName,
                        serviceEndpointId: endpointId
                    },
                    true
                );

                if (externalRepo) {
                    if (Boolean(externalRepo.additionalProperties["hasAdminPermission"]) === true) {
                        const repoId = externalRepo.additionalProperties["nodeId"];
                        if (!this.state.repositories.some(repo => repo.repoId === repoId)) {
                            repo = {
                                name: this.state.repoName,
                                repoId: repoId
                            };
                            this.setState({
                                repoId: repo.repoId
                            });
                        } else {
                            textfieldErrorMessage = Resources.AddRepoManually_itemRepeated;
                        }
                    } else {
                        textfieldErrorMessage = Resources.AddRepoManually_repoNotPermissions;
                    }
                } else {
                    textfieldErrorMessage = Resources.AddRepoManually_repoNotFound;
                }
            } catch (error) {
                error && (textfieldErrorMessage = error.message);
            }

            if (textfieldErrorMessage) {
                this.setState({
                    textfieldErrorMessage: textfieldErrorMessage,
                    isDirty: this.state.repositories.length > 0 || this.state.repoName.length > 0
                });
            }
        }

        return repo;
    };
}
